'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var flat = _interopDefault(require('core-js-pure/features/array/flat'));

/**
 * Notes Module.
 * @module notes
 */

const NOTE_A = 'A';
const NOTE_A_SHARP = 'A#';
const NOTE_A_FLAT = 'Ab';

const NOTE_B = 'B';
const NOTE_B_FLAT = 'Bb';

const NOTE_C = 'C';
const NOTE_C_SHARP = 'C#';

const NOTE_D = 'D';
const NOTE_D_SHARP = 'D#';
const NOTE_D_FLAT = 'Db';

const NOTE_E = 'E';
const NOTE_E_FLAT = 'Eb';

const NOTE_F = 'F';
const NOTE_F_SHARP = 'F#';

const NOTE_G = 'G';
const NOTE_G_SHARP = 'G#';
const NOTE_G_FLAT = 'Gb';

/**
 * Gives chromatic notes starting from C
 */
const CHROMATIC_NOTES = [
  NOTE_C,
  [NOTE_C_SHARP, NOTE_D_FLAT],
  NOTE_D,
  [NOTE_D_SHARP, NOTE_E_FLAT],
  NOTE_E,
  NOTE_F,
  [NOTE_F_SHARP, NOTE_G_FLAT],
  NOTE_G,
  [NOTE_G_SHARP, NOTE_A_FLAT],
  NOTE_A,
  [NOTE_A_SHARP, NOTE_B_FLAT],
  NOTE_B,
];

/**
 * Returns `true` if a note is a sharp note.
 * @example:
 * isSharpNote('D#')  //true
 * isSharpNote('Ab')  //false
 * @function
 * @param {string} note note in string
 * @returns {boolean}
 */
const isSharpNote = (note) => {
  const matches = note.match(/#/);
  return matches && matches.index === 1 && matches[0] === '#';
};

/**
 * Returns `true` if a note is a flat note.
 * @example:
 * isFlatNote('Bb')  //true
 * isFlatNote('C#')  //false
 * @function
 * @param {string} note note in string
 * @returns {boolean}
 */
const isFlatNote = (note) => {
  const matches = note.match(/b/);
  return matches && matches.index === 1 && matches[0] === 'b';
};

/**
 * Returns note name without octave
 * Strips chord information as well
 * @example:
 * getNoteWithoutOctave('C4')  //C
 * getNoteWithoutOctave('F#5')  //F#
 * getNoteWithoutOctave('Db3')  //Db
 * @function
 * @param {string} note note in string with octave
 * @returns {string} note without octave
 */
const getNoteWithoutOctave = (note) => {
  const matches = note.match(/[A-H]/);
  const newNote = matches && matches[0];
  if (isSharpNote(note)) {
    return `${newNote}#`;
  }
  if (isFlatNote(note)) {
    return `${newNote}b`;
  }
  return newNote;
};

/**
 * Returns boolean value if octave number is present in note
 * @example:
 * containsOctaveNumber('C4')  //true
 * containsOctaveNumber('F#5')  //true
 * containsOctaveNumber('Db3')  //true
 * containsOctaveNumber('F')  //false
 * @function
 * @param {string} note note in string
 * @returns {boolean}
 */
const containsOctaveNumber = (note) => /[0-9]/.test(note);

/**
 * Return octave number
 * @example:
 * getOctaveNumber('C4')  //4
 * getOctaveNumber('F#5')  //5
 * @function
 * @param {string} note note in string
 * @returns {number}
 */
const getNoteOctave = (note) => {
  const matches = note.match(/[0-9]/);
  return matches && matches.length && Number(matches.join(''));
};

/**
 * Get note from note index of chromatic notes.
 * @example:
 * getNoteFromIndex({ index: '0' })  //0
 * getNoteFromIndex({ index: '9' })  //A
 * getNoteFromIndex({ index: '10', flatNote: true })  //Bb
 * @function
 * @param {object} {index,flatNote} - Note Information
 * @param {number} options.index - positive index 0-11
 * @param {boolean} options.flatNote - return flat note
 * @returns {string} note
 */
const getNoteFromIndex = ({ index, flatNote }) => {
  const chromaticNote = CHROMATIC_NOTES[index];
  if (Array.isArray(chromaticNote)) {
    if (flatNote) {
      return chromaticNote[1];
    }
    return chromaticNote[0];
  }
  return chromaticNote;
};

/**
 * Returns index of a note from `CHROMATIC_NOTES`.
 * @example:
 * getNoteIndex('C')  //0
 * getNoteIndex('F#')  //6
 * getNoteIndex('Db')  //1
 * getNoteIndex('F')  //5
 * @function
 * @param {string} searchNote note in string
 * @returns {number} Number from 0-11
 */
const getNoteIndex = (searchNote) => {
  let searchIndex = CHROMATIC_NOTES.indexOf(searchNote);

  // if the note is not the one with inharmonic counter part search directly
  if (searchIndex !== -1) {
    return searchIndex;
  }

  CHROMATIC_NOTES.forEach((note, i) => {
    // if is inharmonic val is an array
    if (Array.isArray(note)) {
      if (note.indexOf(searchNote) !== -1) {
        searchIndex = i;
      }
    }
  });

  return searchIndex;
};

/**
 * Get interval between two notes
 * getInterval('D', 'A')  //7
 * getInterval('C', 'Eb')  //3
 * getInterval('C', 'E5')  //3
 * @function
 * @param {note1} note1 note in string
 * @param {note2} note2 note in string
 * @returns {number} interval number
 */
const getInterval = (note1, note2) => {
  let firstNote = note1;
  let secondNote = note2;
  if (containsOctaveNumber(note1)) {
    firstNote = getNoteWithoutOctave(note1);
  }

  if (containsOctaveNumber(note2)) {
    secondNote = getNoteWithoutOctave(note2);
  }

  const note1Index = getNoteIndex(firstNote);
  const note2Index = getNoteIndex(secondNote);
  let noteInterval = 0;
  // A to E
  // 9 to 4 -> 7
  if (note1Index <= note2Index) {
    noteInterval = note2Index - note1Index;
  } else {
    noteInterval = 12 - (note1Index - note2Index);
  }

  const firstNoteOctave = getNoteOctave(note1) || 4;
  const secondNoteOctave = getNoteOctave(note2) || 4;

  if (secondNoteOctave < firstNoteOctave) {
    return 12 - noteInterval;
  }
  if (secondNoteOctave > firstNoteOctave) {
    return 12 + noteInterval;
  }
  return noteInterval;
};

/**
 * Scales Module.
 * @module scales
 */
// import { circleBuilder } from './circle-of-fifths';
// import { arrayRev } from './utils';

/**
 * Scale Formula Array
 * Stores the interval/gaps between notes.
 * Instead of using 0.5 for half and 1 for whole, it would be easier to read and write 1 and 2.
 * 1 => semi tone interval (half step)
 * 2 => whole tone interval (whole step)
 * 3 => 3 half tones (3 half steps) and so on
 */

/**
 * Diatonic to chromatic step map
 * Note number relative to major scale
 * Stores number from 1-8 with flat and sharp information
 * Scale number array 1, 2, 3, 4, 5, 6, 7, 8
 * C, C#, D, D#, E, F, F#, G, G#, A, A#, B, C
 * note num to index map
 * 1 ->0
 * 2 -> 2
 * 3 -> 4
 * 4 -> 5
 * 5 -> 7
 * 6 -> 9
 * 7 -> 11
 * 8 -> 0
 */
const majorScaleIntervalToChromaticIndexMap = {
  1: 0,
  2: 2,
  3: 4,
  4: 5,
  5: 7,
  6: 9,
  7: 11,
  8: 0,
  9: 2,
  10: 4,
  11: 5,
  12: 7,
  13: 9,
  14: 11,
  15: 0,
  16: 2,
  17: 4,
  18: 5,
  19: 7,
  20: 9,
  21: 11,
  22: 0,
  23: 2,
  24: 4,
};

// export const majorIntervals = [0, 2, 4, 5, 7, 9, 11];
// export const minorIntervals = [1, 3, 6, 8, 10];

// TODO: get note based on root note. For e.g. Cm scale the third, sixth and
// seventh should be flat notes is they are enharmonic notes.

/**
 * Note helper for scale. Returns single value from array of inharmonic notes.
 * By default `includeInharmonicNote` is `false`.
 * If it is set to `true`, both notes would be returned.
 * @example
 * getNote(['C#', 'Db'], false, false); //C#
 * getNote(['C#', 'Db'], true, false); //['C#', 'Db']
 * getNote(['C#', 'Db'], false, true); //'Db'
 * @function
 * @param {Object} options - Options Object
 * @param {string} options.chromaticNote - Note in string
 * @param {boolean} options.includeInharmonicNote
 * @param {boolean} options.flatNote
 * @returns {string|array} Can be string or array
 */
const getNote = ({ chromaticNote, isFlatNote }) => {
  if (Array.isArray(chromaticNote)) {
    if (isFlatNote) {
      return chromaticNote[1];
    }
    return chromaticNote[0];
  }
  return chromaticNote;
};

const isFlatInterval = (interval) => /^[b]+/.test(interval);

const isSharpInterval = (interval) => /^[#]+/.test(interval);

/**
 * Get scale interval number
 * @example
 * getNumberFromIntervalString('b3'); //3
 * getNumberFromIntervalString('11'); //11
 * @function
 * @param {string} intervalString - interval in string/number
 * @returns {number}
 */
const getNumberFromIntervalString = (intervalString) => {
  const matches = String(intervalString).match(/[^.b#]/g);
  // if number is greater than 9, it returns two digit num in array like ['1', '1'];
  // concat and cast to Number before returning
  return matches && matches.length && Number(matches.join(''));
};

/**
 * Builds scale based Notes relative number array
 * @example
 * build({
 *  formula: HARMONIC_MINOR_SCALE_NUM,
 *  chromaticNotes: notes.CHROMATIC_NOTES,
 *  note: 'C', includeOctave = true,
 *  numberOfOctaves = 1
 * }); //['C', 'D', 'Eb', 'F', 'G', 'Ab', 'B', 'C']
 * @function
 * @param {Object} options - Options Object
 * @param {string} options.note - Note in string
 * @param {array} options.formula - Formular array
 * @param {array} options.chromaticNotes - chromatic notes
 * @param {boolean} options.includeOctave
 * @param {number} options.numberOfOctaves
 * @param {object} ...rest
 * @returns {array}
 */
const build = ({
  note,
  formula,
  chromaticNotes = CHROMATIC_NOTES,
  includeOctave = true,
  numberOfOctaves = 1,
  /* reverse = false, repeatOctave = false,  minorScaleGroup = false, */
}) => {
  const startNoteIndex = getNoteIndex(note);
  // const sharpNote = notes.isSharpNote(note);
  if (startNoteIndex === -1) {
    throw new Error(`Note Not Found: ${note}`);
  }

  const scaleNotes = [];

  formula.forEach((intervalString, index) => {
    // let nextInterval = intervalFormula[intervalFormula.length - 1] + step;
    // intervalFormula.push(nextInterval);
    if (!includeOctave && index === formula.length - 1) {
      return;
    }
    const num = getNumberFromIntervalString(intervalString);
    let intervalFromRoot = majorScaleIntervalToChromaticIndexMap[num];

    if (isFlatInterval(intervalString)) {
      intervalFromRoot -= 1;
    }

    if (isSharpInterval(intervalString)) {
      intervalFromRoot += 1;
    }

    const noteIndex = (startNoteIndex + intervalFromRoot) % 12;
    const currentNote = getNote({
      chromaticNote: chromaticNotes[noteIndex],
      noteToBuild: note,
      isFlatNote: isFlatNote(note) || isFlatInterval(intervalString),
    });

    scaleNotes.push(currentNote);
  });

  if (numberOfOctaves > 1) {
    let multipleOctaveScale = scaleNotes;
    for (let i = 1; i < numberOfOctaves; i += 1) {
      multipleOctaveScale = multipleOctaveScale.concat(scaleNotes.slice(1));
    }
    // if(reverse) {
    //   return arrayRev({array:multipleOctaveScale, from:(repeatOctave ? 0 : 1)});
    // }
    return multipleOctaveScale;
  }

  // if(reverse) {
  //   return arrayRev({array:scaleNotes, from:(repeatOctave ? 0 : 1)});
  // }
  return scaleNotes;
};

/**
 * Chords Module.
 * Contains most of the chords method
 * @module chords
 */

/**
 * Returns single note for a inharmonic/pure note
 * @private
 * @function
 * @param {object} {note,rootNote,isFlatNote,isSharpNote} options
 * @param {string} options.note note
 * @param {string} options.rootNote note
 * @param {boolean} options.isFlatNote
 * @param {boolean} options.isFlatNote
 * @returns {string} note
 */
const getNote$1 = ({ note, rootNote, isFlatNote: isFlatNote$1, isSharpNote }) => {
  if (Array.isArray(note)) {
    if ((isFlatNote$1 || isFlatNote(rootNote)) && !isSharpNote) {
      return note[1];
    }
    return note[0];
  }
  return note;
};

/**
 * Returns single note for a inharmonic/pure note
 * @private
 * @function
 * @param {object} {chord,inversion} options
 * @param {array} options.chord note
 * @param {number} options.inversion note
 * @returns {array} chord with inversion
 */
const buildChordInversion = ({ chord, inversion = 0 }) => {
  if (inversion === 0 || !inversion) return chord;
  const modulo = chord.length;
  return chord.map(
    (note, index) => chord[(index + (inversion + 1 - 1)) % modulo],
  );
};

/**
 * Build chord from formula
 * @function
 * @param {object} {note,inversion,formula} options
 * @param {string} options.note note
 * @param {number} options.inversion 0-4
 * @param {number} options.octave 0-11
 * @param {array} options.formula formula array
 * @returns {array} chord
 */
const build$1 = ({
  note,
  inversion,
  octave,
  formula,
  baseNote,
  objectResult,
}) => {
  const startNoteIndex = getNoteIndex(note);
  // const sharpNote = notes.isSharpNote(note);
  if (startNoteIndex === -1) {
    throw new Error(`Note Not Found: ${note}`);
  }

  const chordNotes = [];

  formula.forEach((intervalString) => {
    const num = getNumberFromIntervalString(intervalString);

    let intervalFromRoot = majorScaleIntervalToChromaticIndexMap[num];

    if (isFlatInterval(intervalString)) {
      intervalFromRoot -= 1;
    }

    if (isSharpInterval(intervalString)) {
      intervalFromRoot += 1;
    }
    const noteIndex = (startNoteIndex + intervalFromRoot) % 12;
    const currentNote = getNote$1({
      note: CHROMATIC_NOTES[noteIndex],
      rootNote: note,
      isFlatNote:
        isFlatNote(note) || isFlatInterval(intervalString),
      isSharpNote:
        isSharpNote(note) || isSharpInterval(intervalString),
    });

    chordNotes.push(currentNote);
  });

  let result = null;
  let quality = 'major';
  // if second note is flat, minor quality
  if (isFlatInterval(formula[1])) {
    quality = 'minor';
  }

  // if second note & third note is flat, diminished quality
  if (
    isFlatInterval(formula[1]) &&
    formula[2] &&
    isFlatInterval(formula[2])
  ) {
    quality = 'diminished';
  }

  // if third note is sharpened, augmented quality
  if (formula[2] && isSharpInterval(formula[2])) {
    quality = 'augmented';
  }
  // if second note is suspended
  if (formula[1] === 2 || formula[1] === 4) {
    quality = 'sus';
  }

  let inversionNumber = inversion;
  if (baseNote) {
    // C -> C, E, G: root
    // C/E -> E, G, C: 1
    // C/G -> G, C, E: 2

    // D7 -> D F# A C: root
    // D7/F# -> F#, A, C, D: 1
    // D7/A-> A, C, D, F#: 2
    // D7/C-> C, D, F#, A: 3

    const inversionNumberFromBaseNote = chordNotes.indexOf(baseNote);
    // if no baseNote is found in chord, prepend it to the chord notes
    // this also changes chord quality
    if (inversionNumberFromBaseNote === -1) {
      result = buildChordInversion({
        chord: [baseNote, ...chordNotes],
        inversion: 0,
      });
      // In chord A C E G, check [A, C] interval
      const firstSecondNoteInterval = getInterval(
        baseNote,
        chordNotes[0],
      );
      if (firstSecondNoteInterval === 2) {
        quality = 'sus';
      }
      if (firstSecondNoteInterval === 3) {
        quality = 'minor';
      }
      // in chord A C Eb Gb check [A, Eb] interval
      const firstThirdNoteInterval = getInterval(baseNote, chordNotes[1]);
      if (firstThirdNoteInterval === 6) {
        quality = 'diminished';
      }

      if (firstSecondNoteInterval === 4 && firstThirdNoteInterval === 8) {
        quality = 'augmented';
      }
    } else {
      inversionNumber = inversionNumberFromBaseNote;
      result = buildChordInversion({
        chord: chordNotes,
        inversion: inversionNumberFromBaseNote,
      });
    }
  } else {
    result = buildChordInversion({
      chord: chordNotes,
      inversion,
    });
  }

  if (objectResult) {
    const obj = {
      chord: result,
      inversion: inversionNumber,
      quality,
    };
    if (octave) {
      obj.octave = octave;
    }
    return obj;
  }

  return result;
};

/**
 * Chords Parser Module.
 * @module chordsparser
 */

/**
 * Get Chord Symbol
 * @function
 * @param {string} note note with chord symbol inforation e.g. D#Maj7
 * @returns {string} chord symbol
 */
const getChordSymbol = (note) => {
  const matches = note.match(/[^.A-H]/g);
  return matches && matches.join('');
};

/**
 * Get Chord Symbol
 * @example
 * getInversionNumber('Cm7/Eb'); //Eb
 * @function
 * @param {string} chordSymbol chord symbol inforation e.g. D#Maj7/F#
 * @returns {string} chord symbol
 */
const getSlashChordBaseNote = (chordSymbol) => {
  if (/(6\/9)/.test(chordSymbol)) return '';
  if (/\//.test(chordSymbol) === false) return '';
  // match for /G#, /Eb or /2
  const chordParts = chordSymbol.match(/\/[b#0-9A-H]+/);
  const baseNote = chordParts && chordParts.join('').replace('/', '');
  return baseNote;
};

/**
 * Get Octave Number
 * Cm|1 -> 1st octave
 * Bb|3 -> Bb in 3rd octave
 * @example
 * getInversionNumber('Cm7|1'); //1
 * @function
 * @param {string} ChordSymbol chord symbol inforation e.g. Maj7|2
 * @returns {number} octave number
 */
const getOctaveNumber = (chordSymbol) => {
  const octaveRegexp = /\|[0-9]+/;
  if (octaveRegexp.test(chordSymbol) === false) return null;
  const octaveNumber = chordSymbol.match(octaveRegexp);
  const num = octaveNumber && octaveNumber.join('').replace('|', '');
  return Number(num);
};

/**
 * removes # or b symbol from beginning of the chordSymbol
 * warning this doesn't remove #/b from Note name like D# not results D
 * @function
 * @param {string} chordSymbol
 * @returns {string} chord symbol without sharp/flat
 */
const trimSharpFlat = (chordSymbol) =>
  chordSymbol && chordSymbol.replace(/^[#b]+/, '');

const removeRepetition = (chordSymbol) =>
  chordSymbol && chordSymbol.replace(/x[0-9]+/, '');

const trimOctaveNumber = (chordSymbol) => {
  if (!chordSymbol) return '';
  const chordParts = chordSymbol.split('|');
  return chordParts[0];
};

/**
 * Check is base note is inversion number instead
 * @example
 * baseNoteIsInversionNumber('E') -> false
 * baseNoteIsInversionNumber(1) -> true
 * @function
 * @param {string} baseNote
 * @returns {boolean}
 */
const baseNoteIsInversionNumber = (baseNote) =>
  !Number.isNaN(Number(baseNote));

/**
 * Parse from chord symbol
 * New Parser
 * @link https://en.m.wikipedia.org/wiki/Chord_names_and_symbols_(popular_music)#Rules_to_decode_chord_names_and_symbols
 * @function
 * @param {object} {chord,inversion,objectResult,includeChord} options
 * @param {string} {options.chord} chord string
 * @param {number} {options.inversion} inversion num
 * @param {boolean} {options.objectResult} objectResult
 * @param {boolean} {options.includeChord} objectResult
 * @returns {array|object}
 */
const parse = ({ chord, inversion = 0, objectResult, includeChord }) => {
  if (!chord) {
    throw new Error('Chord is Required');
  }

  const noteName = getNoteWithoutOctave(chord.replace(/[()]/g, ''));
  if (!noteName) {
    throw new Error(`Invalid Chord: ${chord}`);
  }
  // TODO use fp compose
  const chordSymbol = trimOctaveNumber(
    removeRepetition(trimSharpFlat(getChordSymbol(chord))),
  );
  let slashChordBaseNote = getSlashChordBaseNote(chord);
  const inversionNumber = baseNoteIsInversionNumber(slashChordBaseNote)
    ? Number(slashChordBaseNote)
    : undefined;
  // if base note is a number, use that number as inversion number
  if (!Number.isNaN(Number(slashChordBaseNote))) {
    slashChordBaseNote = '';
  }
  const octaveNumber = getOctaveNumber(chord);
  if (!chordSymbol) {
    const result = build$1({
      formula: [1, 3, 5],
      note: noteName,
      octave: octaveNumber,
      inversion: inversionNumber || inversion,
      baseNote: slashChordBaseNote,
      objectResult,
    });
    if (includeChord && objectResult) {
      return { ...result, symbol: chord };
    }
    return result;
  }

  // dyad chord
  // C5 -> C, G
  // D#5 -> D#, A#
  // Bb -> Bb, F
  if (chordSymbol === '5') {
    const result = build$1({
      formula: [1, 5],
      note: noteName,
      octave: octaveNumber,
      inversion: inversionNumber || inversion,
      baseNote: slashChordBaseNote,
      objectResult,
    });
    if (includeChord && objectResult) {
      return { ...result, symbol: chord };
    }
    return result;
  }

  // initiate formula with rootNote
  const chordFormula = [1];
  // 2nd number in formula: third note in chord
  // if symbol is one of m, min,o,dim,Ø,- then third note should be flat
  if (/(m|min|o|dim|Ø|-)/.test(chordSymbol) && !/(maj|Maj)/.test(chordSymbol)) {
    chordFormula.push('b3');
  } else if (/(sus2)/.test(chordSymbol)) {
    chordFormula.push(2);
  } else if (/(sus4)/.test(chordSymbol)) {
    chordFormula.push(4);
  } else {
    chordFormula.push(3);
  }

  // 3rd number in formula: fifth note in chord
  if (/(aug|#5|\+|\+5)/.test(chordSymbol)) {
    chordFormula.push('#5');
  } else if (/(o|o5|b5|dim|Ø|o7|dim7|o9|ob9|o11|o13)/.test(chordSymbol)) {
    chordFormula.push('b5');
  } else {
    chordFormula.push(5);
  }
  // 4th number in formula
  // only append if seventh chord or sixth chord

  // sixth chord
  if (/(6|m6)/.test(chordSymbol) && !/(x6)/.test(chordSymbol)) {
    chordFormula.push(6);
  }
  // seventh chord
  if (/(M7|Ma7|Maj7|maj7|M9|M11|M13)/.test(chordSymbol)) {
    chordFormula.push(7);
  } else if (/(o7|dim7|o9|ob9|o11|o13)/.test(chordSymbol)) {
    // bb7 -> double flat 7 = 6
    chordFormula.push(6);
  } else if (
    /(m7|7|Ø7|Ø|9|11|13)/.test(chordSymbol) &&
    !/(6\/9|add9|add11|add13)/.test(chordSymbol)
  ) {
    chordFormula.push('b7');
  }

  // if 7 is included in extended added tone chords
  if (/(7add9|7add11|7add13)/.test(chordSymbol)) {
    chordFormula.push('b7');
  }
  // extended chords

  // ninth
  if (/(b9)/.test(chordSymbol)) {
    chordFormula.push('b9');
  } else if (/(9)/.test(chordSymbol)) {
    chordFormula.push(9);
  }

  // elevenths
  if (/(11)/.test(chordSymbol)) {
    if (!/(add11)/.test(chordSymbol)) {
      chordFormula.push(9);
    }
    chordFormula.push(11);
  }

  // thirteenths
  if (/(13)/.test(chordSymbol)) {
    if (!/(add13)/.test(chordSymbol)) {
      chordFormula.push(9);
      chordFormula.push(11);
    }
    chordFormula.push(13);
  }

  const result = build$1({
    formula: chordFormula,
    note: noteName,
    octave: octaveNumber,
    inversion: inversionNumber || inversion,
    baseNote: slashChordBaseNote,
    objectResult,
  });

  if (includeChord) {
    return { ...result, symbol: chord };
  }
  return result;
};

/**
 * Transpose note up or down chromatically
 * @example
 * getTransposedNote({ note: 'C', num: 1 }); // C#
 * getTransposedNote({ note: 'C', num: -1 }); // B
 * @function
 * @param {object} {note,num} options
 * @param {string} {options.note} note string
 * @param {number} {options.num} transpose up or down
 * @returns {string}
 */
const getTransposedNote = ({ note, num }) => {
  const noteIndex = getNoteIndex(note);
  let newNoteIndex = noteIndex + num;
  // C, -1 -> B
  // C, -2 -> Bb
  if (newNoteIndex < 0) {
    newNoteIndex = Math.abs(12 + num + noteIndex) % 12;
  }
  // B, 1 -> C
  // Bb, 2 -> C
  if (newNoteIndex > 11) {
    newNoteIndex %= 12;
  }

  const transposedNote = getNoteFromIndex({
    index: newNoteIndex,
    flatNote: isFlatNote(note),
  });
  return transposedNote;
};
/**
 * Transpose chord up or down chromatically
 * @example
 * transpose({ chord: 'Cm', num: 1 }); // C#m
 * transpose({ chord: 'C', num: -1 }); // B
 * transpose({ chord: 'B', num: 1 }); // C
 * transpose({ chord: 'Bb', num: -1 }); // A
 * @function
 * @param {object} {chord,num} options
 * @param {string} {options.chord} chord string
 * @param {number} {options.num} transpose up or down in scale
 * @returns {string}
 */
const transpose = ({ chord, num = 1 }) => {
  if (!chord) {
    throw new Error('No chord to transpose');
  }
  const sanitizedChord = chord.replace(/[^.A-H#b]/, '');
  const note = getNoteWithoutOctave(sanitizedChord);
  let slashChordBaseNote = getSlashChordBaseNote(chord);

  if (baseNoteIsInversionNumber(slashChordBaseNote)) {
    slashChordBaseNote = null;
  }
  const transposedNote = getTransposedNote({ note, num });
  let transposedBaseNote = null;
  if (slashChordBaseNote) {
    transposedBaseNote = getTransposedNote({ note: slashChordBaseNote, num });
  }
  const oldChordSymbol = chord;
  let newChordSymbol = oldChordSymbol.replace(note, transposedNote);
  if (transposedBaseNote) {
    // append / infront to avoid main chord note being replaced by base note
    // e.g. in C/G,7 transpose without appending / -> D/G
    // e.g. in C/G,7 transpose with appending / -> G/D
    newChordSymbol = newChordSymbol.replace(
      `/${slashChordBaseNote}`,
      `/${transposedBaseNote}`,
    );
  }
  return newChordSymbol;
};

/**
 * Modes Module.
 * @module modes
 */

// diatonic modes
// major quality
const IONIAN = [1, 2, 3, 4, 5, 6, 7, 8];

/*
TODO
Modes for harmonic and melodic minor scales
*/

/*
TODO
modes for pentatonic scales
*/

/**
 * Progression Module.
 * @module progression
 */

/**
 * Build progression from chords of a scale
 * @function
 * @param {object} {chords,progression} options object
 * @param {array} options.chords chords formed from scale
 * @param {progression} options.progression formula e.g. [1, 6, 4, 5]
 * @returns {array} progression array
 */
const build$2 = ({ chords, progression }) => {
  const result = progression.map((chordIndex) => chords[chordIndex - 1]);
  return result;
};

const phraseRepetitionRegexp = /\)x[0-9]+/;
/**
 * Get Repetition Number for a phrase
 * (Cm Bb)x2 -> 2
 * @example
 * getPhraseRepetitionNumber('(Cm Bb)x2'); //2
 * @function
 * @param {string} progression phrase inforation e.g. (Cm Bb)x2
 * @returns {number} phrase repetition number
 */
const getPhraseRepetitionNumber = (chordSymbol) => {
  if (phraseRepetitionRegexp.test(chordSymbol) === false) return null;
  const repetitionNumber = chordSymbol.match(phraseRepetitionRegexp);
  const num = repetitionNumber && repetitionNumber.join('').replace(')x', '');
  return Number(num);
};

/**
 * Get Chord Repetition Number
 * Cm|1 -> first inversion of Cm chord
 * @example
 * getChordRepetitionNumber('Cm7|1'); //1
 * @function
 * @param {string} ChordSymbol chord symbol inforation e.g. Maj7|2
 * @returns {number} repetition number
 */
const getChordRepetitionNumber = (chordSymbol) => {
  const repetitionRegexp = /x[0-9]+/;
  if (repetitionRegexp.test(chordSymbol) === false) return null;
  const repetitionNumber = chordSymbol.match(repetitionRegexp);
  const num = repetitionNumber && repetitionNumber.join('').replace('x', '');
  return Number(num);
};

const isRest = (chordSymbol) => {
  const restRegexp = /^0[x0-9]*/;
  return restRegexp.test(chordSymbol);
};

const getRest = (chordSymbol) => {
  const restRegexp = /^0[x0-9]+/;
  if (!isRest(chordSymbol)) return null;
  const rest = chordSymbol.match(restRegexp);
  return rest && rest.join('');
};

// separates progression by new line
const getProgressionByNewLine = (progression) => {
  const lineSeparatorRegexp = /[\r]+/g;
  // const sanitizationRegexp = /[\s]+/g;
  const sanitizedString = progression.replace(lineSeparatorRegexp, ' ').trim();

  // remove comments, lyrics
  // if a line starts with // then it is a comment
  const lines = sanitizedString.split(/[\n]+/g);
  return lines;
};

// const lyricRegexp = /^\/\/[A-Za-z-_,;.!?\[\]'"\s]+/g;
const lyricRegexp = /^\/\//;
// TODO comment can be a normal sentence
// user typing // is a bad user experience
const isLyricLine = (line) => {
  return lyricRegexp.test(line.trim());
};

// https://stackoverflow.com/
// questions/9849754/how-can-i-replace-newlines-line-breaks-with-spaces-in-javascript
/* eslint no-control-regex: "off" */
/* eslint no-useless-escape: "off" */
const sanitizationRegexp = /[\[\]\s\s\r\n\x0B\x0C\u0085\u2028\u2029]+/g;
const getLinesAsObjectArray = (progression) => {
  return getProgressionByNewLine(progression)
    .filter((line) => line.trim().replace(sanitizationRegexp, ''))
    .map((line) => {
      if (isLyricLine(line)) {
        return { isComment: true, comment: line.trim() };
      }
      return { isComment: false, chords: line.trim() };
    });
};

/**
 * Get Chords and Lyrics by line for a progression
 * @example
 * getChordsAndLyrics(`
 *  G D
 * // Lyric one
 * Em C
 * // lyric two
 `);
 * // [chord, lyric, chord, lyric]
 * @function
 * @param {string} progression
 * @returns {array} chord and lyric array
 */
const getChordsAndLyrics = (progression) => {
  const lines = getLinesAsObjectArray(progression);
  let chordPosition = 0;
  const result = lines.map((lineObject) => {
    if (lineObject.isComment) {
      return {
        ...lineObject,
        comment: lineObject.comment.replace(lyricRegexp, ''),
      };
    }

    const chordsStringArray = lineObject.chords
      .replace(sanitizationRegexp, ' ')
      .split(' ');

    return chordsStringArray.map((chordWithSymbol) => {
      if (isRest(chordWithSymbol)) {
        const chordResult = {
          rest: true,
          originalSymbol: chordWithSymbol,
          chordPosition,
        };
        chordPosition += 1;
        return chordResult;
      }
      const chordResult = {
        chordPosition,
        chord: chordWithSymbol
          .replace(/[\(]/, '')
          .replace(phraseRepetitionRegexp, ''),
        originalSymbol: chordWithSymbol,
      };
      chordPosition += 1;
      return chordResult;
    });
  });
  return result;
};
/**
 * Build progression string of chords separated by space
 * Supports individual chord repetition, phrase repetitions
 * @example
 * parse('C F G7') //[{chord: 'C', 'E', 'G'], ...},{chord:['F', 'A', 'C'],...}, {chord: ['G', 'B', 'D', 'F'],...}]
 * @function
 * @param {string} progression string
 * @returns {array} progression array
 */
const parse$1 = (progression) => {
  const lines = getLinesAsObjectArray(progression)
    .filter((line) => !line.isComment)
    .map((lineObject) => lineObject.chords);

  const linesString = lines.join(' ');

  // final sanitization
  // replace any left over words that looks like a chord
  // for example GRAPE is not removed by lyricRegexp,
  // but AM7 should not be removed because it contains two cap letters
  // if there are consecutive three or more cap letters they are not chord symbols
  let sanitizedString = linesString
    .replace(/[A-Z]{3,}/g, '')
    .replace(sanitizationRegexp, ' ');

  // phrase repetitions regex
  // looks for (Gm Am)x2
  const phraseRepetitionRegex = /\(([^)]+)\)x[0-9]+/g;
  // looks for only subphrase without repetition number
  const subPhraseRegexp = /\(([^)]+)\)/g;
  const phraseRepetitions = sanitizedString.match(phraseRepetitionRegex);

  // check for phrase repetitions
  // if there are phrase repetitions, multiply the phrase with the
  // repetition number
  if (phraseRepetitions && phraseRepetitions.length) {
    phraseRepetitions.forEach((phraseWithRepetition, index) => {
      const phraseRepetitionNumber = getPhraseRepetitionNumber(
        phraseWithRepetition,
      );
      if (phraseRepetitionNumber === 0) {
        throw new Error('0 is not a valid phrase repetition');
      }
      const subPhrase = phraseWithRepetition
        .match(subPhraseRegexp)
        .join('')
        .replace(/(\(|\))/, '');
      const multipliedPhrase = Array(phraseRepetitionNumber)
        .fill(`__${index}__${subPhrase}`)
        .join(' ');
      sanitizedString = sanitizedString.replace(
        phraseWithRepetition,
        multipliedPhrase,
      );
    });
  }
  // console.log('sanitizedString', sanitizedString);
  const repeatedPhraseStartRegexp = /^__[0-9]+__/;

  // after all necessary sanitation and subphrase parsing, separate individual chords
  const chordsStringArray = sanitizedString.trim().split(' ');

  let chordPosition = 0;
  let groupChordPosition = 0;
  let prevRepeatitionNumber = -1;

  // parse individual chords
  const result = chordsStringArray.map((chordWithSymbol) => {
    const parseChordOptions = {
      chord: chordWithSymbol.replace(repeatedPhraseStartRegexp, ''),
      objectResult: true,
      includeChord: true,
    };
    if (repeatedPhraseStartRegexp.test(chordWithSymbol)) {
      const phraseRepeatetionNumber = Number(chordWithSymbol.split('__')[1]);
      if (phraseRepeatetionNumber !== prevRepeatitionNumber) {
        // change groupChordPosition when repeatition phrase changes
        groupChordPosition = chordPosition;
      }
      chordPosition = groupChordPosition;
      prevRepeatitionNumber = phraseRepeatetionNumber;
    }

    if (isRest(chordWithSymbol)) {
      const restRepetitionNumber =
        getChordRepetitionNumber(chordWithSymbol) || 1;
      const resultObject = Array(restRepetitionNumber).fill({
        rest: true,
        repetition: restRepetitionNumber,
        chordPosition,
      });
      chordPosition += 1;
      return resultObject;
    }

    const parsedChord = parse(parseChordOptions);
    const chordRepetition = getChordRepetitionNumber(chordWithSymbol);
    // if (chordRepetition === 0) {
    //   throw new Error('0 is not a valid chord repetition');
    // }
    const getChordObject = (repetition) => {
      if (repetition) {
        return {
          ...parsedChord,
          chordPosition,
          repetition,
        };
      }

      return {
        ...parsedChord,
        chordPosition,
      };
    };
    const resultObject = Array(chordRepetition || 1).fill(
      getChordObject(chordRepetition),
    );
    chordPosition += 1;
    return resultObject;
  });

  return flat(result);
};

/**
 * Transpose progression
 * @example
 * transpose({ progression: 'C F G7', num: 2 })  //D G A7
 * @function
 * @param {object} {progression,objectResult} options
 * @param {string} {options.progression} progression string
 * @param {number} {options.num} num to transpose
 * @returns {string} transposed progression string
 */
const transpose$1 = ({ progression, num = 1 }) => {
  const lines = getLinesAsObjectArray(progression)
    .map((line) => {
      if (line.isComment) return line;

      const chordsStringArray = line.chords
        .replace(sanitizationRegexp, ' ')
        .split(' ');

      return {
        chords: chordsStringArray
          .map((chord) => {
            if (isRest(chord)) {
              return chord;
            }
            return transpose({
              chord,
              num,
            });
          })
          .filter((chord) => chord),
      };
    })
    .map((line) => {
      if (line.isComment) {
        return line.comment;
      }
      return line.chords.map((chord) => chord).join(' ');
    })
    .join('\n');

  return lines;
};

const isLowerCaseRomanNumeral = (numeral) => {
  const test = /[i|v]/.test(numeral);
  return test;
};

const isUpperCaseRomanNumeral = (numeral) => {
  const test = /[I|V]/.test(numeral);
  return test;
};

const romanToNumberMap = {
  i: 1,
  ii: 2,
  iii: 3,
  iv: 4,
  v: 5,
  vi: 6,
  vii: 7,
  viii: 8,
};

/**
 * Get number from roman number
 * @example
 * romanToNumber('i') -> 1
 * romanToNumber('vi') -> 6
 * @function
 * @param {string} roman number
 * @returns {number} number
 */
const romanToNumber = (roman) => romanToNumberMap[roman.toLowerCase()];

const getRomanNumber = (note) => {
  const matches = note.match(/[.i|v]/gi);
  return matches && matches.join('');
};

const getChordSymbol$1 = (note) => {
  const matches = note.match(/[^.i|v]/gi);
  return matches && matches.join('');
};

// Roman inversion
// export const getInversionNumber = (note) => {
//   const matches = note.match(/(b|c|d)/);
// };

/**
 * Build progression from scale with roman numeral numbering
 * @example
 * parseRoman({tonic: 'C', progression: ['I', 'V', 'viio', 'ii']})
 * //[['C', 'E', 'G'], ['G', 'B', 'D'], ['B', 'D', 'F'], ['D', 'F', 'A']]
 * @function
 * @param {object} {tonic,progression} options object
 * @param {string} options.tonic tonic note
 * @param {array} options.mode mode number formula
 * @param {string|array} options.progression formula e.g. ['i', 'VI', 'III', 'VI']
 * @returns {array} progression array
 */
const parseRoman = ({
  tonic,
  mode = IONIAN,
  progression,
  objectResult = false,
}) => {
  let chordsStringArray = [];
  if (Array.isArray(progression)) {
    chordsStringArray = progression;
  } else {
    chordsStringArray = progression.split(' ');
  }

  const result = chordsStringArray.map((romanSymbol) => {
    const chordObject = {
      chordRomanNumber: getRomanNumber(romanSymbol),
      chordSymbol: getChordSymbol$1(romanSymbol),
    };
    chordObject.chordNumber = romanToNumber(chordObject.chordRomanNumber);
    chordObject.isMinor = isLowerCaseRomanNumeral(chordObject.chordRomanNumber);

    return chordObject;
  });

  const scale = build({ note: tonic, formula: mode });

  const chords = result.map((romanObject) => {
    const noteIndex = romanObject.chordNumber - 1;
    const note = scale[noteIndex];
    const noteWithChordSymbol =
      note +
      (romanObject.isMinor ? 'm' : '') +
      (romanObject.chordSymbol ? romanObject.chordSymbol : '');
    return parse({
      chord: noteWithChordSymbol,
      objectResult,
    });
  });
  return chords;
};

exports.build = build$2;
exports.getChordRepetitionNumber = getChordRepetitionNumber;
exports.getChordSymbol = getChordSymbol$1;
exports.getChordsAndLyrics = getChordsAndLyrics;
exports.getLinesAsObjectArray = getLinesAsObjectArray;
exports.getPhraseRepetitionNumber = getPhraseRepetitionNumber;
exports.getProgressionByNewLine = getProgressionByNewLine;
exports.getRest = getRest;
exports.getRomanNumber = getRomanNumber;
exports.isLowerCaseRomanNumeral = isLowerCaseRomanNumeral;
exports.isLyricLine = isLyricLine;
exports.isRest = isRest;
exports.isUpperCaseRomanNumeral = isUpperCaseRomanNumeral;
exports.parse = parse$1;
exports.parseRoman = parseRoman;
exports.romanToNumber = romanToNumber;
exports.romanToNumberMap = romanToNumberMap;
exports.transpose = transpose$1;
